'use client'

import { PropsWithChildren, createContext, useContext, useState, useEffect, useRef } from 'react'
import { setCookie } from 'cookies-next'
import { useApi } from '@/contexts/ApiContext'
import { useAuth } from '@/contexts/AuthContext'
import { usePathname } from 'next/navigation'
import { throttle } from 'lodash'
import { User } from '@/types/interfaces/user'
import { reportErrorToDD } from '@/lib/analytics/datadogError'
import { onDatadogRumReady } from '@/lib/analytics/datadogRum'
import {
    isAuthenticatedViaMiddlewareCookie,
    MIDDLEWARE_AUTH_COOKIE,
} from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { isAnySignupPath } from '@/lib/namedRoutes'
import { clearAuthenticatedApiCache } from '@/lib/clearAuthenticatedApiCache'
import { clearPendingSignupSsn } from '@/lib/pendingSignupSsn'
import { redactCarePartnerLinkTokens } from '@/lib/analytics/carePartnerTelemetryScrub'

// localStorage keys cleared on logout. The first block is legacy Verified
// 1-click / about-you profile fields that may still be written on the
// manual signup fallback; the second block is session/profile state that
// should not survive a sign-out.
const LOGOUT_LOCAL_STORAGE_KEYS = [
    'firstName',
    'lastName',
    'email',
    'birthDate',
    'gender',
    'line1',
    'line2',
    'city',
    'state',
    'zipCode',
    'isVerify',
    'phoneNumber',
    'patientId',
    'genai_conversation_session_id',
    'signup_complete_tracked',
    'currentLoginSignupStep',
    'totalStepCount',
] as const

interface UserContextData {
    fetchUser: (forceReload?: boolean) => Promise<User | null> | undefined
    logoutUser: () => Promise<void>
    user: User | null
    setDatadogUserId: (id: string, extra?: { signupSkuId?: string }) => void
    refreshPatient: () => Promise<void>
}

const UserContext = createContext<UserContextData>({
    fetchUser: async (forceReload: boolean = false) => null,
    logoutUser: async () => undefined,
    user: null,
    setDatadogUserId: () => undefined,
    refreshPatient: async () => undefined,
})

function markSignedOutInMiddlewareCookie() {
    const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN
    setCookie(MIDDLEWARE_AUTH_COOKIE, 'false', {
        path: '/',
        sameSite: 'lax',
        secure: process.env.NODE_ENV !== 'development',
        ...(cookieDomain ? { domain: cookieDomain } : {}),
    })
}

export default function UserProvider({ children }: PropsWithChildren) {
    const { apiGet, apiPost } = useApi()
    const [user, setUser] = useState<User | null>(null)
    const { logout } = useAuth()
    const pathname = usePathname()
    const pageViewSentRef = useRef(false)
    const prevPathRef = useRef(pathname)

    const fetchUser = throttle(
        async (forceReload: boolean = false): Promise<User | null> => {
            if (!forceReload && user) {
                return user
            }

            if (isAnySignupPath(pathname)) {
                // Patient record may not exist yet during signup/login — skip to avoid 404
                return null
            }

            // Check if user is authenticated via cookie set by middleware
            // This is the source of truth - middleware knows if cookies are valid
            if (!isAuthenticatedViaMiddlewareCookie()) {
                // Middleware says user is not authenticated, skip API call to avoid 401
                return null
            }

            try {
                // Fetch user info from Cognito and Nile Core
                const userResponse = await apiGet('/external/patient')
                const userProfile: User = {
                    id: userResponse.id,
                    firstName: userResponse.first_name,
                    lastName: userResponse.last_name,
                    email: userResponse.email,
                    phoneNumber: userResponse.phone_number,
                    dateOfBirth: userResponse.date_of_birth,
                    sexAssignedAtBirth: userResponse.sex_assigned_at_birth,
                    homeAddress: Object.values(userResponse.home_address).join(' '),
                    homeAddressObject: userResponse.home_address,
                    homeAddressTimezone: userResponse.home_address_time_zone,
                    idVerified: userResponse.id_verified,
                    idVerifiedAt: userResponse.id_verified_at || null,
                    athenaId: userResponse.athena_id,
                    signupSkuId: userResponse.signup_sku_id,
                    blocked: userResponse.blocked,
                }
                window.localStorage.setItem('patientId', userResponse.id)
                // Notify LaunchDarkly provider that patientId changed
                window.dispatchEvent(new Event('patientIdChanged'))
                setDatadogUserId(userResponse.id, {
                    signupSkuId: userResponse.signup_sku_id,
                })
                setUser(userProfile)
                segmentIdentify(userProfile?.id || undefined)
                return userProfile
            } catch (error) {
                console.error('Error fetching user info:', error)
                reportErrorToDD(error, { source: 'UserContext', action: 'fetchUser' })
                throw error
            }
        },
        2000,
        { trailing: false },
    )

    const setDatadogUserId = (
        id: string,
        extra?: { signupSkuId?: string },
    ) => {
        onDatadogRumReady((datadogRum) => {
            datadogRum.setUser({
                id,
                ...(extra?.signupSkuId && { signupSkuId: extra.signupSkuId }),
            })
        })
    }

    const segmentIdentify = (userId?: string) => {
        if (!window.analytics) {
            return
        }

        window.analytics.ready(function () {
            if (!window.analytics.user().id() && userId) {
                window.analytics.identify(userId, {
                    id: userId,
                })
            }
        })
    }

    useEffect(() => {
        const handleRouteChange = () => {
            const patientId = window.localStorage.getItem('patientId')
            // Only fetch user if middleware says user is authenticated
            // Middleware is the source of truth - it checks the httpOnly cookies
            if (isAuthenticatedViaMiddlewareCookie()) {
                if (patientId) {
                    setDatadogUserId(patientId)
                    segmentIdentify(patientId)
                }

                if (!user) {
                    fetchUser()?.catch(() => {})
                }
            }
        }

        if (window.analytics && (!pageViewSentRef.current || prevPathRef.current !== pathname)) {
            // Care-partner link tokens are the only questionnaire credential; never send them to Segment/Datadog.
            const pageViewAttributes = {
                path: redactCarePartnerLinkTokens(window.location.pathname),
                url: redactCarePartnerLinkTokens(window.location.href),
                title: document.title,
                referrer: redactCarePartnerLinkTokens(
                    pageViewSentRef.current
                        ? new URL(prevPathRef.current, window.location.origin).href
                        : document.referrer,
                ),
                search: window.location.search,
            }
            console.debug('🔍 Segment page view:', pageViewAttributes)
            window.analytics.page(pageViewAttributes)
            pageViewSentRef.current = true
            prevPathRef.current = pathname
        }

        // Check initial route
        handleRouteChange()

        // The effect will re-run whenever the pathname changes or user state changes
    }, [pathname, fetchUser, user])

    const refreshPatient = async () => {
        await apiPost('/external/patients/refresh')
    }

    const logoutUser = async () => {
        // Flip the client-readable auth mirror first so the user-null effect does not
        // call /external/patient while httpOnly session cookies are still valid. That
        // request's middleware response would Set-Cookie accessToken again (maxAge 600)
        // and undo /auth/logout.
        markSignedOutInMiddlewareCookie()
        try {
            await logout()
        } finally {
            setUser(null)
            // Clear cached patient/session keys so a subsequent user on the same
            // device cannot inherit the previous user's PII. Profile fields may
            // still be written on the manual about-you fallback; the rest are
            // session-scoped state that should not survive logout. Non-account
            // state (analytics SDKs, cart, etc.) is intentionally left in place.
            for (const key of LOGOUT_LOCAL_STORAGE_KEYS) {
                window.localStorage.removeItem(key)
            }
            clearAuthenticatedApiCache()
            clearPendingSignupSsn()
            // Notify LaunchDarkly provider that patientId changed (reverts to anonymous)
            window.dispatchEvent(new Event('patientIdChanged'))
            sessionStorage.clear()
        }
    }

    return (
        <UserContext.Provider value={{ fetchUser, user, logoutUser, setDatadogUserId, refreshPatient }}>
            {children}
        </UserContext.Provider>
    )
}

export const useUserContext = () => useContext(UserContext)
